import constant from "../../constant";
import router from "../../router";

const api = constant.api + "";
const url = constant.url + "";

function safeJsonParse(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return value;
  }
}

const state = () => ({
  authentications: [],
  user: safeJsonParse(localStorage.getItem(constant.currentUser)) || {},
  usernaneError: "",
  passwordError: "",
  error: "",
  userRole: localStorage.getItem(constant.userRoleKey) || "",
  settings: safeJsonParse(localStorage.getItem(constant.appSettings)) || [],
});

const getters = {
  user: (state) => state.user,
  getAuthData: (state) => state.user,
  getUsernaneError: (state) => state.usernaneError,
  getPasswordError: (state) => state.passwordError,
  getError: (state) => state.error,
  getAllAuthentications: (state) => state.authentications,
  getUserRole: (state) => state.userRole,

  // Dynamic getter for settings
  getSetting: (state) => (key) => {
    const fromStorage = localStorage.getItem(key);
    if (fromStorage !== null) return safeJsonParse(fromStorage);

    const setting = Array.isArray(state.settings)
      ? state.settings.find((s) => s && s.key === key)
      : null;
    return (setting && setting.value) || null;
  },

  // Loop through all settings keys and return them dynamically
  allSettings: (state) => {
    const settingsObject = {};
    state.settings.forEach((setting) => {
      settingsObject[setting.key] = setting.value;
    });
    return settingsObject;
  },
};

const actions = {
  async login({ commit }, data) {
    try {
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        throw new Error("API instance not available. Please refresh the page.");
      }

      // Start loading animation from plugin
      if (this._vm.$loading && this._vm.$loading.start) {
        this._vm.$loading.start();
      }

      // Get CSRF cookie
      await axiosInstance.get(url + "sanctum/csrf-cookie");

      // Prepare login payload with IP and device info
      // Supports both:
      // - username + password (classic login)
      // - username + token (SSO / redirect login)
      const loginPayload = {
        username: data.username,
      };

      if (data.token) {
        loginPayload.token = data.token;
        commit("SET_PASSWORD_ERROR", "");
      } else {
        loginPayload.password = data.password;
      }

      // Add IP address and device info if provided
      if (data.ip_address) {
        loginPayload.ip_address = data.ip_address;
      }
      if (data.device_info) {
        loginPayload.device_info = data.device_info;
      }

      // Use axios instance directly for login (no confirmation needed)
      const response = await axiosInstance.post(api + "auth/login", loginPayload);
      const { status, message, username, user, settings, token } =
        response.data;

      switch (status) {
        case "usernameError":
          commit("SET_USERNAME_ERROR", message);
          commit("SET_PASSWORD_ERROR", "");
          commit("SET_ERROR", "");
          throw Object.assign(new Error(message || "usernameError"), {
            loginStatus: "usernameError",
          });

        case "passwordError":
          commit("SET_PASSWORD_ERROR", message);
          commit("SET_USERNAME_ERROR", "");
          commit("SET_ERROR", "");
          throw Object.assign(new Error(message || "passwordError"), {
            loginStatus: "passwordError",
          });

        case "error":
          commit("SET_ERROR", message);
          commit("SET_USERNAME_ERROR", "");
          commit("SET_PASSWORD_ERROR", "");
          throw Object.assign(new Error(message || "error"), {
            loginStatus: "error",
          });

        case "confirmationCode":
          router.push({ name: "login-confirmation-code", query: { username } });
          break;

        /*
        case "success":
          // Set data in localStorage
          localStorage.setItem(
            constant.authToken,
            typeof token === 'string' ? token : JSON.stringify(token)
          );
          localStorage.setItem(constant.currentUser, JSON.stringify(user));

          // Loop through settings and store each key-value pair in localStorage
          settings.forEach((setting) => {
            localStorage.setItem(
              setting.key,
              setting.valueType === "json"
                ? JSON.parse(setting.value)
                : setting.value
            );
          });

          // Store settings object as well
          localStorage.setItem(constant.appSettings, JSON.stringify(settings));

          // Update Vuex state
          commit("SET_TOKEN", token);
          commit("SET_USER", user);
          commit("SET_SETTINGS", settings);

          if (user.role) {
            await commit("SET_USER_ROLE", user.role.fullName);
            await commit("SYNC_STATE_WITH_LOCAL_STORAGE");
          }
 
          setTimeout(() => {
            router.push({ name: "dashboard" });
          }, 1000);

          break;*/

        case "success":
          // Set data in localStorage
          // Store token as plain string (not JSON stringified) to avoid double quotes
          localStorage.setItem(constant.authToken, typeof token === 'string' ? token : JSON.stringify(token));
          localStorage.setItem(constant.currentUser, JSON.stringify(user));

          // 🔐 Vérification ici avant d'utiliser settings
          if (Array.isArray(settings)) {
            settings.forEach((setting) => {
              const parsed =
                setting.valueType === "json"
                  ? safeJsonParse(setting.value)
                  : setting.value;
              localStorage.setItem(
                setting.key,
                setting.valueType === "json"
                  ? JSON.stringify(parsed)
                  : String(parsed === null || parsed === undefined ? "" : parsed)
              );
            });

            localStorage.setItem(
              constant.appSettings,
              JSON.stringify(settings)
            );
            commit("SET_SETTINGS", settings);
          }

          // Update Vuex state
          commit("SET_TOKEN", token);
          commit("SET_USER", user);

          if (user.role) {
            await commit("SET_USER_ROLE", user.role.fullName);
            await commit("SYNC_STATE_WITH_LOCAL_STORAGE");
          }

          setTimeout(() => {
            window.location.replace("/dashboard");
            // router.push({ name: "dashboard" });
          }, 1000);

          break;
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // Handle IP authorization error
      if (error.response && error.response.status === 403) {
        const errorMessage = (error.response.data && error.response.data.message) || 
          'Cet appareil n\'est pas autorisé. Veuillez contacter l\'administrateur.';
        commit("SET_ERROR", errorMessage);
        commit("SET_USERNAME_ERROR", "");
        commit("SET_PASSWORD_ERROR", "");
      } else if (error.response && error.response.data) {
        // Handle other API errors
        const responseData = error.response.data;
        if (responseData.status === 'usernameError') {
          commit("SET_USERNAME_ERROR", responseData.message);
          commit("SET_PASSWORD_ERROR", "");
        } else if (responseData.status === 'passwordError') {
          commit("SET_PASSWORD_ERROR", responseData.message);
          commit("SET_USERNAME_ERROR", "");
        } else if (responseData.status === 'error') {
          commit("SET_ERROR", responseData.message);
          commit("SET_USERNAME_ERROR", "");
          commit("SET_PASSWORD_ERROR", "");
        }
      }
      // Re-throw to allow component to handle
      throw error;
    } finally {
      // Stop loading animation from plugin
      if (this._vm.$loading && this._vm.$loading.stop) {
        this._vm.$loading.stop();
      }
    }
  },

  syncStateWithLocalStorage({ commit }) {
    commit("SYNC_STATE_WITH_LOCAL_STORAGE");
  },

  async validateConfirmationCode({ commit }, data) {
    try {
      const response = await this.$api.create(
        api + "auth/confirmation/code",
        data,
        { confirm: false }
      );
      const { status, user, token } = response.data;

      if (status === "success") {
        // Update Vuex state
        commit("SET_TOKEN", token);
        commit("SET_USER", user);

        if (user.role) {
          await commit("SET_USER_ROLE", user.role.fullName);
          await commit("SYNC_STATE_WITH_LOCAL_STORAGE");
        }

        setTimeout(() => {
          window.location.replace("/dashboard");
          // router.push({ name: "dashboard" });
        }, 1000);
      }
    } catch (error) {
      console.error("Error while validating confirmation code", error);
    }
  },

  async resendConfirmationCode({ commit }, data) {
    try {
      // Get axios instance from Vue plugin
      const axiosInstance = this._vm.$api && this._vm.$api.instance 
        ? this._vm.$api.instance 
        : null;

      if (!axiosInstance) {
        throw new Error("API instance not available. Please refresh the page.");
      }

      // Start loading animation from plugin
      if (this._vm.$loading && this._vm.$loading.start) {
        this._vm.$loading.start();
      }

      const response = await axiosInstance.post(
        api + "auth/confirmation/code/resend",
        data
      );
      const { status, message } = response.data;

      commit("notification/store", { status, message }, { root: true });
    } catch (error) {
      console.error("Resend confirmation code error:", error);
    } finally {
      // Stop loading animation from plugin
      if (this._vm.$loading && this._vm.$loading.stop) {
        this._vm.$loading.stop();
      }
    }
  },

  async logout({ commit }) {
    localStorage.removeItem(constant.currentUser);
    localStorage.removeItem(constant.authToken);
    localStorage.removeItem(constant.userRoleKey);

    // Update state
    commit("SET_USER", {});
    commit("SET_USER_ROLE", "");

    await router.push({ name: "login" });
  },

  fetchAllAuthentications({ commit }) {
    // Get axios instance from Vue plugin
    const axiosInstance = this._vm.$api && this._vm.$api.instance 
      ? this._vm.$api.instance 
      : null;

    if (!axiosInstance) {
      console.error("API instance not available");
      return;
    }

    axiosInstance
      .get(api + "auth")
      .then((response) => {
        commit("SET_ALL_AUTHENTICATIONS", response.data);
      })
      .catch((error) => {
        console.error("Error fetching authentications:", error);
      });
  },
};

const mutations = {
  SYNC_STATE_WITH_LOCAL_STORAGE(state) {
    const userStr = localStorage.getItem(constant.currentUser);
    const roleStr = localStorage.getItem(constant.userRoleKey);
    const tokenStr = localStorage.getItem(constant.authToken);
    const settingsStr = localStorage.getItem(constant.appSettings);

    if (userStr) {
      state.user = safeJsonParse(userStr) || {};
    }

    if (roleStr) {
      state.userRole = roleStr;
    }

    if (settingsStr) {
      state.settings = safeJsonParse(settingsStr) || [];
    }
  },

  SET_USER_ROLE(state, value) {
    state.userRole = value;
    localStorage.setItem(constant.userRoleKey, value);
  },

  SET_USER(state, value) {
    state.user = value;
    localStorage.setItem(constant.currentUser, JSON.stringify(value));
  },

  SET_SETTINGS(state, value) {
    state.settings = value;
    localStorage.setItem(constant.appSettings, JSON.stringify(value));
  },

  SET_TOKEN(state, value) {
    localStorage.setItem(constant.authToken, typeof value === 'string' ? value : JSON.stringify(value));
  },

  SET_USERNAME_ERROR(state, value) {
    state.usernaneError = value;
  },

  SET_PASSWORD_ERROR(state, value) {
    state.passwordError = value;
  },

  SET_ERROR(state, value) {
    state.error = value;
  },

  SET_ALL_AUTHENTICATIONS(state, value) {
    state.authentications = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
